import type { ApolloError } from '@apollo/client';

import type { ErrorInfo } from '../notifications/types';

/**
 * Extracts meaningful error information from Apollo errors
 */
export function extractErrorInfo(error: ApolloError): ErrorInfo {
  // Check for GraphQL errors first
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    const graphQLError = error.graphQLErrors[0];
    return {
      message: graphQLError.message,
      code: graphQLError.extensions?.code as string,
      path: graphQLError.path?.map((p) => String(p)),
      isGraphQLError: true,
      isNetworkError: false,
    };
  }

  // Check for network errors
  if (error.networkError) {
    const { networkError } = error;
    return {
      message: networkError.message || 'Network error occurred',
      isGraphQLError: false,
      isNetworkError: true,
    };
  }

  // Fallback to generic error message
  return {
    message: error.message || 'An unexpected error occurred',
    isGraphQLError: false,
    isNetworkError: false,
  };
}

/**
 * Formats error messages for user-friendly display
 */
export function formatErrorMessage(errorInfo: ErrorInfo): string {
  // Common error code mappings
  const errorCodeMessages: Record<string, string> = {
    FORBIDDEN: 'You do not have permission to perform this action',
    UNAUTHORIZED: 'Please log in to continue',
    NOT_FOUND: 'The requested resource was not found',
    VALIDATION_ERROR: 'Please check your input and try again',
    INTERNAL_ERROR: 'An internal error occurred. Please try again later',
    BAD_REQUEST: 'Invalid request. Please check your input and try again',
    UNAUTHENTICATED: 'Please log in to continue',
  };

  // Use custom message if available
  if (errorInfo.code && errorCodeMessages[errorInfo.code]) {
    return errorCodeMessages[errorInfo.code];
  }

  // For network errors, provide user-friendly message
  if (errorInfo.isNetworkError) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Fallback to original message but clean it up
  return errorInfo.message || 'An unexpected error occurred. Please try again.';
}
